/**
 * geo_strategy slide layout: chosen deterministically from the market count,
 * never by the agent. Pure; type-only imports so the web bundle and vitest
 * share it through `@shared/geo-layout`.
 */
import type { AudienceSegment, GeoArea, GeoStrategy, Geography } from './types.js';

export type GeoLayoutKind =
  /** One full-bleed map centered on the advertiser (the original layout). */
  | 'single'
  /** One tall tile per market, side by side (2, 3 or 5 markets). */
  | 'columns'
  /** Even grid of tiles (4 = 2x2, 6 = 3x2). */
  | 'grid'
  /** One US map with a pin per market plus a ranked list (7+ markets or states). */
  | 'map-list'
  /** National: one full-bleed detailed map fitted to the contiguous US, priority markets pinned on it. */
  | 'national'
  /** One state: one full-bleed detailed map fitted to the state. */
  | 'state';

export interface GeoLayout {
  kind: GeoLayoutKind;
  columns: number;
  rows: number;
  /** True only for strategy === 'national': the headline area reads nationwide. */
  nationwide: boolean;
  /** True for strategy === 'state': tiles / the map are fitted to and tint each state. */
  byState: boolean;
}

/** Largest market count that still gets one map tile per market. */
export const MAX_TILED_MARKETS = 6;

/**
 * `areaCount` is the number of target areas: markets for metro-style
 * strategies, STATES for strategy 'state' (see `targetAreaCount`).
 */
export function pickGeoLayout(strategy: GeoStrategy | null, areaCount: number): GeoLayout {
  if (strategy === 'national') return { kind: 'national', columns: 1, rows: 1, nationwide: true, byState: false };
  const byState = strategy === 'state';
  const n = Number.isFinite(areaCount) ? Math.max(0, Math.floor(areaCount)) : 0;
  const base = { nationwide: false, byState };
  if (n === 0) return { kind: 'single', columns: 1, rows: 1, ...base, byState: false };
  if (n === 1) return byState ? { kind: 'state', columns: 1, rows: 1, ...base } : { kind: 'single', columns: 1, rows: 1, ...base };
  if (n > MAX_TILED_MARKETS) return { kind: 'map-list', columns: 1, rows: 1, ...base };
  if (n === 4) return { kind: 'grid', columns: 2, rows: 2, ...base };
  if (n === 6) return { kind: 'grid', columns: 3, rows: 2, ...base };
  return { kind: 'columns', columns: n, rows: 1, ...base };
}

/** Areas that drive the layout: only the 'state' areas under strategy 'state', else all of them. */
export function targetAreas(geography: Pick<Geography, 'strategy' | 'areas'>): GeoArea[] {
  return geography.strategy === 'state' ? geography.areas.filter((a) => a.kind === 'state') : geography.areas;
}

/** Default sizing radius, mirrors DEFAULT_AUDIENCE_RADIUS_MILES in src/agent/tools.ts. */
export const DEFAULT_SIZING_RADIUS_MILES = 10;

/**
 * The radius the audience was actually sized with for one market, per
 * `segmentSizingTargets` (src/agent/tools.ts): a segment's own override, else
 * the area's, else the store radius, else 10 miles. Segments can differ; the
 * map draws the widest so the circle covers every count shown.
 */
export function marketRadiusMiles(
  area: Pick<GeoArea, 'radiusMiles'>,
  geography: Pick<Geography, 'storeRadiusMiles'>,
  segments: Pick<AudienceSegment, 'geography'>[],
): number {
  const fallback = area.radiusMiles ?? geography.storeRadiusMiles ?? DEFAULT_SIZING_RADIUS_MILES;
  const radii = segments.map((s) => s.geography?.radiusMiles ?? fallback).filter((r) => Number.isFinite(r) && r > 0);
  return radii.length > 0 ? Math.max(...radii) : fallback;
}

/**
 * People reached in one market: the same transparent-sum rule the deck's
 * total audience uses (`aggregateSegmentEstimates`, src/audience/segments.ts)
 * - every segment must be currently sized, and every one must carry a count
 * for the label - else null. Never a guess, never 0 for "unknown".
 */
export function marketPeopleCount(
  segments: Pick<AudienceSegment, 'sizingState' | 'byLocation'>[],
  label: string,
): number | null {
  if (segments.length === 0) return null;
  let total = 0;
  for (const segment of segments) {
    if (segment.sizingState !== 'sized') return null;
    const entry = segment.byLocation?.find((b) => b.label === label);
    if (!entry || !Number.isFinite(entry.count)) return null;
    total += entry.count;
  }
  return total;
}
